// File: /var/www/app2/frontend/src/components/Dashboard.tsx

import React, { useState, useEffect } from "react";
import axios from "axios";
import { User } from "../App";
import "../styles/Dashboard.css";
import DashboardPDFModal, { PDFFilterSelection } from "./DashboardPDFModal";
import generateDashboardPDF, { DashboardPDFData } from "../utils/generateDashboardPDF";

interface DashboardProps {
  user: User;
  activeCountryId?: number | null;
}

interface DashboardStats {
  totalReports: number;
  pendingReview: number;
  approved: number;
  rejected: number;
  currentMonthSubmissions: number;
  totalMissionaries: number;
  activeMissionaries: number;
  submittedThisMonth: number;
  pendingSubmissions: number;
  submissionRate: number;
}

interface MinistryPeriodStats {
  heardGospel?: number;
  receivedJesus?: number;
  baptized?: number;
  bibleStudies?: number;
  discoveryBibleStudyStarted?: number;
  churchesPlanted?: number;
  leadersTrained?: number;
}

interface MinistryStats {
  currentMonth?: MinistryPeriodStats;
  priorMonth?: MinistryPeriodStats;
  ytd?: MinistryPeriodStats;
  changes?: MinistryPeriodStats;
}

interface CurrentPeriod {
  monthName?: string;
  year?: number;
}

interface Missionary {
  id: number;
  name: string;
  number: string;
  country?: string;
  state?: string;
  is_active?: boolean;
}

function Dashboard({ user, activeCountryId }: DashboardProps) {
  const isAdmin = user.role === 'admin' || user.role === 'superadmin' || user.role === 'system_admin';
  const noCountry = activeCountryId === null || activeCountryId === undefined;

  const [stats, setStats] = useState<DashboardStats>({
    totalReports: 0,
    pendingReview: 0,
    approved: 0,
    rejected: 0,
    currentMonthSubmissions: 0,
    totalMissionaries: 0,
    activeMissionaries: 0,
    submittedThisMonth: 0,
    pendingSubmissions: 0,
    submissionRate: 0,
  });
  const [ministryStats, setMinistryStats] = useState<MinistryStats>({
    currentMonth: {},
    priorMonth: {},
    ytd: {},
    changes: {},
  });
  const [, setCurrentPeriod] = useState<CurrentPeriod>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Filter states
  const currentYear = new Date().getFullYear();
  const [filterYear, setFilterYear] = useState<string | number>(currentYear);
  const [filterMonth, setFilterMonth] = useState<string>('');
  const [filterState, setFilterState] = useState<string>('');
  const [filterMissionary, setFilterMissionary] = useState<string>('');
  const [missionaries, setMissionaries] = useState<Missionary[]>([]);
  const [states, setStates] = useState<string[]>([]);

  // PDF Modal state
  const [showPDFModal, setShowPDFModal] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  useEffect(() => {
    if (noCountry) {
      setLoading(false);
      return;
    }
    fetchDashboardStats();
    fetchMissionaries();
    fetchStates();
    const interval = setInterval(fetchDashboardStats, 30000);
    return () => clearInterval(interval);
  }, [activeCountryId, filterYear, filterMonth, filterState, filterMissionary]);

  const fetchDashboardStats = async () => {
    try {
      const params = new URLSearchParams();
      if (filterYear) params.append('year', filterYear.toString());
      if (filterMonth) params.append('month', filterMonth);
      if (filterState) params.append('state', filterState);
      if (filterMissionary) params.append('missionaryId', filterMissionary);

      const response = await axios.get(`/api/dashboard/stats?${params.toString()}`);
      if (response.data.success) {
        setStats(response.data.stats);
        setMinistryStats(response.data.ministryStats || {});
        setCurrentPeriod(response.data.currentPeriod || {});
      }
    } catch (err: any) {
      if (err?.response?.status !== 409) {
        console.error("Failed to fetch dashboard stats:", err);
        setError("Failed to load dashboard statistics");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchMissionaries = async () => {
    try {
      const url = isAdmin ? '/api/missionaries' : `/api/users/${user.id}/missionaries`;
      const response = await axios.get(url);
      if (response.data.success) {
        setMissionaries(response.data.missionaries || []);
      }
    } catch (err: any) {
      if (err?.response?.status !== 409) console.error("Failed to fetch missionaries:", err);
    }
  };

  const fetchStates = async () => {
    try {
      const response = await axios.get('/api/missionaries/states');
      if (response.data.success) {
        setStates(response.data.states || []);
      }
    } catch (err: any) {
      if (err?.response?.status !== 409) console.error("Failed to fetch states:", err);
    }
  };

  const formatNumber = (num?: number): string => {
    return new Intl.NumberFormat().format(num || 0);
  };

  const getChangeIndicator = (change?: number): React.ReactElement => {
    if (!change) {
      return <span className="change-indicator neutral">→ 0%</span>;
    }
    if (change > 0) {
      return <span className="change-indicator positive">↑ {change}%</span>;
    }
    return (
      <span className="change-indicator negative">↓ {Math.abs(change)}%</span>
    );
  };

  const handleDownloadPDF = async (filters: PDFFilterSelection) => {
    setIsGeneratingPDF(true);
    try {
      // Fetch fresh data with the selected filters
      const params = new URLSearchParams();
      params.append('year', String(filters.year));
      if (filters.month) params.append('month', filters.month);
      if (filters.state) params.append('state', filters.state);
      if (filters.missionaryId) params.append('missionaryId', filters.missionaryId);

      const response = await axios.get(`/api/dashboard/stats?${params.toString()}`);

      if (response.data.success) {
        const pdfData: DashboardPDFData = {
          stats: response.data.stats,
          ministryStats: response.data.ministryStats || {},
          currentPeriod: response.data.currentPeriod || {},
          filters: {
            year: filters.year,
            month: filters.month,
            state: filters.state,
            missionaryId: filters.missionaryId,
            missionaryName: filters.missionaryName,
          },
          generatedBy: user.fullName || user.username,
          isAdmin,
        };

        await generateDashboardPDF(pdfData);
        setShowPDFModal(false);
      } else {
        console.error("Failed to fetch dashboard data for PDF");
        alert("Failed to generate PDF. Please try again.");
      }
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("An error occurred while generating the PDF. Please try again.");
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  if (noCountry) {
    return (
      <div className="dashboard-container" style={{ paddingTop: 60, textAlign: "center" }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🌍</div>
        <h2 style={{ color: "#1a3a35", marginBottom: 8 }}>Select a Country</h2>
        <p style={{ color: "#555", fontSize: 15 }}>
          Use the <strong>Country</strong> selector in the top navigation bar to choose which country you'd like to view.
        </p>
      </div>
    );
  }

  if (loading) {
    return <div className="loading-container">Loading dashboard...</div>;
  }

  if (error) {
    return (
      <div className="error-container">
        <h2 className="dashboard-title">Dashboard</h2>
        <div className="error-message">{error}</div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h2 className="dashboard-title">{isAdmin ? 'Horn of Africa Dashboard' : 'Supporter Dashboard'}</h2>
        <div className="dashboard-header-right">
          <button
            className="download-pdf-btn"
            onClick={() => setShowPDFModal(true)}
            title="Download Dashboard as PDF"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ marginRight: '6px' }}
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            Download PDF
          </button>
        </div>
      </div>

      {/* Report Review Status (admin only) */}
      {isAdmin && (
        <div>
          <h3 className="section-header">Report Review Status</h3>
          <div className="status-grid">
            <div className="status-card">
              <h4>TOTAL REPORTS</h4>
              <div className="status-value">{stats.totalReports}</div>
              <div className="status-subtitle">All time</div>
            </div>

            <div className="status-card">
              <h4>PENDING REVIEW</h4>
              <div className="status-value pending">{stats.pendingReview}</div>
              <div className="status-subtitle">Needs attention</div>
            </div>

            <div className="status-card">
              <h4>APPROVED</h4>
              <div className="status-value approved">{stats.approved}</div>
              <div className="status-subtitle">
                {stats.totalReports > 0
                  ? Math.round((stats.approved / stats.totalReports) * 100)
                  : 0}% approval rate
              </div>
            </div>

            <div className="status-card">
              <h4>THIS MONTH</h4>
              <div className="status-value primary">
                {stats.currentMonthSubmissions}
              </div>
              <div className="status-subtitle">Reports submitted</div>
            </div>
          </div>
        </div>
      )}

      {/* Year to Date Ministry Impact */}
      <div>
        <h3 className="section-header">Ministry Impact Statistics</h3>

        {/* Filters */}
        <div className="ministry-filters" style={{ marginBottom: '20px', display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <label style={{ fontSize: '12px', marginBottom: '5px', color: '#333', fontWeight: 600 }}>Year</label>
            <select
              value={filterYear}
              onChange={(e) => setFilterYear(e.target.value)}
              style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
            >
              {[2024, 2025, 2026, 2027].map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <label style={{ fontSize: '12px', marginBottom: '5px', color: '#333', fontWeight: 600 }}>Month</label>
            <select
              value={filterMonth}
              onChange={(e) => setFilterMonth(e.target.value)}
              style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
            >
              <option value="">All Months</option>
              <option value="1">January</option>
              <option value="2">February</option>
              <option value="3">March</option>
              <option value="4">April</option>
              <option value="5">May</option>
              <option value="6">June</option>
              <option value="7">July</option>
              <option value="8">August</option>
              <option value="9">September</option>
              <option value="10">October</option>
              <option value="11">November</option>
              <option value="12">December</option>
            </select>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <label style={{ fontSize: '12px', marginBottom: '5px', color: '#333', fontWeight: 600 }}>State</label>
            <select
              value={filterState}
              onChange={(e) => setFilterState(e.target.value)}
              style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
            >
              <option value="">All States</option>
              {states.map(state => (
                <option key={state} value={state}>{state}</option>
              ))}
            </select>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <label style={{ fontSize: '12px', marginBottom: '5px', color: '#333', fontWeight: 600 }}>Missionary</label>
            <select
              value={filterMissionary}
              onChange={(e) => setFilterMissionary(e.target.value)}
              style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ddd', minWidth: '200px' }}
            >
              <option value="">All Missionaries</option>
              {missionaries.map(missionary => (
                <option key={missionary.id} value={missionary.id}>
                  {missionary.name} ({missionary.number})
                </option>
              ))}
            </select>
          </div>

          <button
            onClick={() => {
              setFilterYear(currentYear);
              setFilterMonth('');
              setFilterState('');
              setFilterMissionary('');
            }}
            style={{
              padding: '8px 16px',
              borderRadius: '4px',
              border: '1px solid #ddd',
              background: '#f5f5f5',
              cursor: 'pointer',
              alignSelf: 'flex-end'
            }}
          >
            Reset Filters
          </button>
        </div>

        <div className="ministry-grid">
          {/* People Who Heard Gospel */}
          <div className="ministry-card">
            <h4>PEOPLE REACHED</h4>
            <div className="ministry-metrics">
              <div className="ministry-value gospel">
                {formatNumber(ministryStats.currentMonth?.heardGospel)}
              </div>
              {getChangeIndicator(ministryStats.changes?.heardGospel)}
            </div>
            <div className="ministry-comparison">
              <div>
                Prior: {formatNumber(ministryStats.priorMonth?.heardGospel)}
              </div>
              <div>YTD: {formatNumber(ministryStats.ytd?.heardGospel)}</div>
            </div>
          </div>

          {/* People Who Received Jesus */}
          <div className="ministry-card">
            <h4>CONVERSIONS</h4>
            <div className="ministry-metrics">
              <div className="ministry-value conversions">
                {formatNumber(ministryStats.currentMonth?.receivedJesus)}
              </div>
              {getChangeIndicator(ministryStats.changes?.receivedJesus)}
            </div>
            <div className="ministry-comparison">
              <div>
                Prior: {formatNumber(ministryStats.priorMonth?.receivedJesus)}
              </div>
              <div>YTD: {formatNumber(ministryStats.ytd?.receivedJesus)}</div>
            </div>
          </div>

          {/* People Baptized */}
          <div className="ministry-card">
            <h4>BAPTISMS</h4>
            <div className="ministry-metrics">
              <div className="ministry-value baptisms">
                {formatNumber(ministryStats.currentMonth?.baptized)}
              </div>
              {getChangeIndicator(ministryStats.changes?.baptized)}
            </div>
            <div className="ministry-comparison">
              <div>
                Prior: {formatNumber(ministryStats.priorMonth?.baptized)}
              </div>
              <div>YTD: {formatNumber(ministryStats.ytd?.baptized)}</div>
            </div>
          </div>

          {/* Bible Studies */}
          <div className="ministry-card">
            <h4>BIBLE STUDIES</h4>
            <div className="ministry-metrics">
              <div className="ministry-value studies">
                {formatNumber(
                  ministryStats.currentMonth?.bibleStudies ||
                    ministryStats.currentMonth?.discoveryBibleStudyStarted
                )}
              </div>
              {getChangeIndicator(
                ministryStats.changes?.bibleStudies ||
                  ministryStats.changes?.discoveryBibleStudyStarted
              )}
            </div>
            <div className="ministry-comparison">
              <div>
                Prior:{" "}
                {formatNumber(
                  ministryStats.priorMonth?.bibleStudies ||
                    ministryStats.priorMonth?.discoveryBibleStudyStarted
                )}
              </div>
              <div>
                YTD:{" "}
                {formatNumber(
                  ministryStats.ytd?.bibleStudies ||
                    ministryStats.ytd?.discoveryBibleStudyStarted
                )}
              </div>
            </div>
          </div>

          {/* Churches Planted */}
          <div className="ministry-card">
            <h4>CHURCHES</h4>
            <div className="ministry-metrics">
              <div className="ministry-value churches">
                {formatNumber(ministryStats.currentMonth?.churchesPlanted)}
              </div>
              {getChangeIndicator(ministryStats.changes?.churchesPlanted)}
            </div>
            <div className="ministry-comparison">
              <div>
                Prior: {formatNumber(ministryStats.priorMonth?.churchesPlanted)}
              </div>
              <div>YTD: {formatNumber(ministryStats.ytd?.churchesPlanted)}</div>
            </div>
          </div>

          {/* Leaders Trained */}
          <div className="ministry-card">
            <h4>LEADERS</h4>
            <div className="ministry-metrics">
              <div className="ministry-value leaders">
                {formatNumber(ministryStats.currentMonth?.leadersTrained)}
              </div>
              {getChangeIndicator(ministryStats.changes?.leadersTrained)}
            </div>
            <div className="ministry-comparison">
              <div>
                Prior: {formatNumber(ministryStats.priorMonth?.leadersTrained)}
              </div>
              <div>YTD: {formatNumber(ministryStats.ytd?.leadersTrained)}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Missionary Overview / My Missionaries */}
      <div>
        <h3 className="section-header">{isAdmin ? 'Missionary Overview' : 'My Missionaries'}</h3>
        {isAdmin ? (
          <div className="missionary-grid">
            <div className="missionary-card total">
              <h4>Total Missionaries</h4>
              <div className="missionary-value">{stats.totalMissionaries}</div>
            </div>

            <div className="missionary-card submitted">
              <h4>Submitted This Month</h4>
              <div className="missionary-value">{stats.submittedThisMonth}</div>
            </div>

            <div className="missionary-card pending">
              <h4>Pending Submissions</h4>
              <div className="missionary-value">{stats.pendingSubmissions}</div>
            </div>

            <div className="missionary-card rate">
              <h4>Submission Rate</h4>
              <div className="missionary-value">{stats.submissionRate}%</div>
              <div className="progress-container">
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{ width: `${stats.submissionRate}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="missionary-grid">
            <div className="missionary-card total">
              <h4>Active Missionaries</h4>
              <div className="missionary-value">{missionaries.filter(m => m.is_active !== false).length}</div>
            </div>
            {(() => {
              const activeMissionaries = missionaries.filter(m => m.is_active !== false);
              const countryCounts: Record<string, number> = {};
              activeMissionaries.forEach(m => {
                const c = m.country || 'Unknown';
                countryCounts[c] = (countryCounts[c] || 0) + 1;
              });
              return Object.entries(countryCounts).sort(([a], [b]) => a.localeCompare(b)).map(([country, count]) => (
                <div key={country} className="missionary-card submitted">
                  <h4>{country}</h4>
                  <div className="missionary-value">{count}</div>
                </div>
              ));
            })()}
          </div>
        )}
      </div>

      {/* PDF Download Modal */}
      <DashboardPDFModal
        isOpen={showPDFModal}
        onClose={() => setShowPDFModal(false)}
        onDownload={handleDownloadPDF}
        currentFilters={{
          year: filterYear,
          month: filterMonth,
          state: filterState,
          missionaryId: filterMissionary,
        }}
        missionaries={missionaries}
        states={states}
        isGenerating={isGeneratingPDF}
      />
    </div>
  );
}

export default Dashboard;
